import { warn } from '@execonline-inc/logging';
import { InvalidUrlError, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import GroupsOptionsStore from '.';
import { AppyError, callApi } from '../../Appy';
import { handleError } from '../../ErrorActionableReaction';
import { Error } from '../../ErrorHandling';
import { MissingLinkError, findLinkT } from '../../LinkyLinky';
import ReactionComponent, { RCProps } from '../../ReactionComponent';
import { Link } from '../../Resource/Types';
import { fromNullableT } from '../../TaskExt';
import { groupsOptionsResourceDecoder } from './Decoders';
import { State } from './Types';

interface Props extends RCProps<GroupsOptionsStore> {}

type OptionsError = AppyError | MissingLinkError | InvalidUrlError | Error;

const handleOptionsError = (store: GroupsOptionsStore) => (error: OptionsError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
    case 'error':
      store.error('We are unable to process your request at this time');
      warn('GroupsOptionsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const buildUrl = (participant: string, link: Link): Task<OptionsError, Link> =>
  toUrlT(link.href)
    .map(putQueryParam('participant', participant))
    .map((url): Link => ({ ...link, href: url.href }));

const sendRequest = callApi(groupsOptionsResourceDecoder, {});

const modifyOrReturnLink = (store: GroupsOptionsStore) => (link: Link) =>
  Task.succeed<OptionsError, {}>({})
    .andThen(() =>
      fromNullableT(store.groupsStore.searchParameters.find((p) => p.key === 'participants')),
    )
    .andThen((participant) => buildUrl(participant.value, link))
    .orElse(() => Task.succeed<OptionsError, Link>(link));

class GroupsOptionsReactions extends ReactionComponent<GroupsOptionsStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<OptionsError, ReadonlyArray<Link>>(store.groupsStore.links)
          .andThen(findLinkT('options'))
          .andThen(modifyOrReturnLink(store))
          .andThen(sendRequest)
          .fork(handleOptionsError(store), store.ready);
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default GroupsOptionsReactions;
